"use client"

import type React from "react"

import { useEffect, useState, useRef, type FormEvent } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Form, Spinner, Alert } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { ArrowLeft, Send, Paperclip, XCircle, FileText, ImageIcon, File } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getTicketById, addTicketComment, clearCurrentTicket } from "../../redux/slices/ticketsSlice"
import { formatDate } from "../../utils/timestamp"
import { useAuth } from "../../contexts/auth-context"
import AttachmentPreviewModal from "../../components/AttachmentPreviewModal"

export default function TicketThreadPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { currentTicket, isLoading, error } = useAppSelector((state) => state.tickets)
  const { user } = useAuth()
  const [newComment, setNewComment] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [attachment, setAttachment] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [selectedAttachment, setSelectedAttachment] = useState<{
    url: string
    name: string
    type: string
  } | null>(null)

  useEffect(() => {
    if (ticketId) {
      dispatch(getTicketById(ticketId))
    }

    return () => {
      dispatch(clearCurrentTicket())
    }
  }, [dispatch, ticketId])

  useEffect(() => {
    scrollToBottom()
  }, [currentTicket?.comments])

  const scrollToBottom = () => {
    setTimeout(() => {
      const isMobile = window.innerWidth < 768

      if (isMobile) {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        })
      } else {
        const desktopContainer = document.querySelector(".d-none.d-md-flex .overflow-auto")
        if (desktopContainer) {
          desktopContainer.scrollTop = desktopContainer.scrollHeight
          return
        }
      }

      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        })
      }
    }, 200)
  }

  const handleSubmitComment = async (e: FormEvent) => {
    e.preventDefault()
    if ((!newComment.trim() && !attachment) || !ticketId) return

    setSubmitting(true)
    try {
      await dispatch(addTicketComment({ ticketId, content: newComment, attachment })).unwrap()
      setNewComment("")
      setAttachment(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    } catch (error) {
      console.error("Failed to add comment:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0])
    }
  }

  const clearAttachment = () => {
    setAttachment(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Helper function to determine file type icon
  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon className="size-4" />
    } else if (fileType.includes("pdf")) {
      return <FileText className="size-4" />
    } else {
      return <File className="size-4" />
    }
  }

  const handleAttachmentClick = (url: string, name: string, type: string) => {
    setSelectedAttachment({ url, name, type })
    setShowAttachmentModal(true)
  }

  if (isLoading && !currentTicket) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        {error}
      </Alert>
    )
  }

  if (!currentTicket) {
    return (
      <Alert variant="warning" className="my-3">
        {t("ticketNotFound")}
      </Alert>
    )
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="d-md-none" style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        <div
          className="p-3 d-flex align-items-center gap-3"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1030,
            height: "70px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <button className="btn-back-circle" onClick={() => navigate(`/dashboard/tickets/${ticketId}`)}>
            <ArrowLeft size={20} />
          </button>
          <div className="flex-grow-1">
            <h1 className="fs-6 fw-bold mb-0 text-white text-truncate">{currentTicket.title}</h1>
            <div className="d-flex gap-1">
              <span className="badge bg-primary small">{t(currentTicket.type)}</span>
              <span className={`badge small ${currentTicket.status === "open" ? "bg-warning" : "bg-success"}`}>
                {t(currentTicket.status)}
              </span>
            </div>
          </div>
        </div>

        <div
          className="flex-grow-1 overflow-auto px-3"
          style={{
            paddingTop: "20px",
            marginBottom: currentTicket?.status === "open" ? "230px" : "40px",
          }}
        >
          <div className="d-flex flex-column gap-3 py-3">
            {currentTicket.comments && currentTicket.comments.length > 0 ? (
              currentTicket.comments.map((comment) => {
                const isCurrentUser = comment.userId === user?.id
                const isSystemMessage = comment.isSystemMessage

                if (isSystemMessage) {
                  return (
                    <div key={comment.id} className="text-center my-2">
                      <span
                        className="badge py-2 px-3 text-white"
                        style={{
                          background: "rgba(255, 255, 255, 0.2)",
                          backdropFilter: "blur(10px)",
                        }}
                      >
                        {comment.content}
                      </span>
                    </div>
                  )
                }

                return (
                  <div key={comment.id} className={`d-flex ${isCurrentUser ? "justify-content-end" : ""}`}>
                    <div className="d-flex gap-2" style={{ maxWidth: "85%" }}>
                      {!isCurrentUser && (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white overflow-hidden flex-shrink-0"
                          style={{
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            minHeight: "40px",
                            background: "rgba(255, 255, 255, 0.2)",
                          }}
                        >
                          {comment.userPhotoURL ? (
                            <img
                              src={comment.userPhotoURL || "/placeholder.svg"}
                              alt={comment.userFullName}
                              className="w-100 h-100 rounded-circle"
                              style={{ objectFit: "cover" }}
                            />
                          ) : (
                            comment.userFullName.charAt(0).toUpperCase()
                          )}
                        </div>
                      )}
                      <div
                        className={`rounded-3 p-3 ${isCurrentUser ? "text-primary" : "text-white"}`}
                        style={{
                          background: isCurrentUser ? "white" : "rgba(255, 255, 255, 0.15)",
                          backdropFilter: "blur(10px)",
                        }}
                      >
                        <div className="d-flex justify-content-between gap-2 mb-1">
                          <span className="small fw-medium">
                            {comment.userFullName}
                            {comment.userRole === "admin" && <span className="badge bg-danger ms-1 small">Admin</span>}
                          </span>
                          <span className="small opacity-75">{formatDate(comment.createdAt)}</span>
                        </div>
                        {comment.content && (
                          <p className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                            {comment.content}
                          </p>
                        )}

                        {comment.hasAttachment && (
                          <div className="mt-2">
                            <div
                              onClick={() =>
                                handleAttachmentClick(
                                  comment.attachmentUrl || "",
                                  comment.attachmentName || "Attachment",
                                  comment.attachmentType || "",
                                )
                              }
                              className={`d-flex align-items-center gap-2 p-2 rounded cursor-pointer ${isCurrentUser ? "bg-white text-primary" : "bg-primary-subtle"}`}
                              style={{ cursor: "pointer" }}
                            >
                              {getFileIcon(comment.attachmentType || "")}
                              <span className="small text-truncate">{comment.attachmentName || "Attachment"}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {isCurrentUser && (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white overflow-hidden flex-shrink-0"
                          style={{
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            minHeight: "40px",
                            background: "rgba(255, 255, 255, 0.2)",
                          }}
                        >
                          {comment.userPhotoURL ? (
                            <img
                              src={comment.userPhotoURL || "/placeholder.svg"}
                              alt={comment.userFullName}
                              className="w-100 h-100 rounded-circle"
                              style={{ objectFit: "cover" }}
                            />
                          ) : (
                            comment.userFullName.charAt(0).toUpperCase()
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-center text-white py-5">
                <p className="mb-0 opacity-75">No comments yet</p>
                <small className="opacity-50">Start the conversation</small>
              </div>
            )}
          </div>
          <div ref={messagesEndRef} />
        </div>

        {currentTicket?.status === "open" && (
          <div
            className="py-3 px-2"
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1030,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Form onSubmit={handleSubmitComment} className="px-3">
              <Form.Group className="mb-2">
                <Form.Control
                  as="textarea"
                  rows={4} // doubled from 2 to 4 rows for mobile
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Type your message"
                  disabled={submitting}
                  className="bg-white border-0 rounded-3"
                  size="lg"
                />
              </Form.Group>

              {attachment && (
                <div
                  className="mb-3 p-2 rounded-3 d-flex justify-content-between align-items-center"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 text-white">
                    {getFileIcon(attachment.type)}
                    <span className="small text-truncate">{attachment.name}</span>
                  </div>
                  <button className="p-0 text-white" onClick={clearAttachment}>
                    <XCircle className="size-4" />
                  </button>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center">
                <button
                  className="btn-cancel-outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting}
                >
                  <Paperclip className="size-4 me-1" />
                  Attach
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                />

                <button
                  type="submit"
                  className="btn-white-pill-bold"
                  disabled={(!newComment.trim() && !attachment) || submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      {t("sending")}
                    </>
                  ) : (
                    <>
                      <Send className="size-4 me-2" />
                      {t("sendMessage")}
                    </>
                  )}
                </button>
              </div>
            </Form>
          </div>
        )}
      </div>

      {/* Desktop Layout */}
      <div className="d-none d-md-flex flex-column py-4 px-4" style={{ height: "calc(100vh - 8rem)" }}>
        <div className="d-flex align-items-center gap-3 mb-4">
          <button className="btn-back-circle" onClick={() => navigate(`/dashboard/tickets/${ticketId}`)}>
            <ArrowLeft size={20} />
          </button>
          <div className="flex-grow-1">
            <h1 className="fs-3 fw-bold mb-1 text-white">{currentTicket.title}</h1>
            <div className="d-flex gap-2">
              <span className="badge bg-primary">{t(currentTicket.type)}</span>
              <span className={`badge ${currentTicket.status === "open" ? "bg-warning" : "bg-success"}`}>
                {t(currentTicket.status)}
              </span>
            </div>
          </div>
        </div>

        <div
          className="flex-grow-1 d-flex flex-column rounded-4 overflow-hidden"
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            minHeight: "400px",
          }}
        >
          <div className="p-3 border-bottom border-white border-opacity-25">
            <h2 className="fs-5 mb-0 text-white">Conversation</h2>
          </div>

          <div className="flex-grow-1 overflow-auto p-4" style={{ maxHeight: "calc(100vh - 300px)" }}>
            <div className="d-flex flex-column gap-4">
              {currentTicket.comments && currentTicket.comments.length > 0 ? (
                currentTicket.comments.map((comment) => {
                  const isCurrentUser = comment.userId === user?.id
                  const isSystemMessage = comment.isSystemMessage

                  if (isSystemMessage) {
                    return (
                      <div key={comment.id} className="text-center my-3">
                        <span
                          className="badge py-2 px-4 text-white"
                          style={{
                            background: "rgba(255, 255, 255, 0.2)",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          {comment.content}
                        </span>
                      </div>
                    )
                  }

                  return (
                    <div key={comment.id} className={`d-flex ${isCurrentUser ? "justify-content-end" : ""}`}>
                      <div className="d-flex gap-3" style={{ maxWidth: "75%" }}>
                        {!isCurrentUser && (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white overflow-hidden flex-shrink-0"
                            style={{
                              width: "45px",
                              height: "45px",
                              minWidth: "45px",
                              minHeight: "45px",
                              background: "rgba(255, 255, 255, 0.2)",
                            }}
                          >
                            {comment.userPhotoURL ? (
                              <img
                                src={comment.userPhotoURL || "/placeholder.svg"}
                                alt={comment.userFullName}
                                className="w-100 h-100 rounded-circle"
                                style={{ objectFit: "cover" }}
                              />
                            ) : (
                              comment.userFullName.charAt(0).toUpperCase()
                            )}
                          </div>
                        )}
                        <div
                          className={`rounded-3 p-3 ${isCurrentUser ? "text-primary" : "text-white"}`}
                          style={{
                            background: isCurrentUser ? "white" : "rgba(255, 255, 255, 0.15)",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          <div className="d-flex justify-content-between gap-3 mb-2">
                            <span className="fw-medium">
                              {comment.userFullName}
                              {comment.userRole === "admin" && <span className="badge bg-danger ms-2">Admin</span>}
                            </span>
                            <span className="small opacity-75">{formatDate(comment.createdAt)}</span>
                          </div>
                          {comment.content && (
                            <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                              {comment.content}
                            </p>
                          )}

                          {comment.hasAttachment && (
                            <div className="mt-3">
                              <div
                                onClick={() =>
                                  handleAttachmentClick(
                                    comment.attachmentUrl || "",
                                    comment.attachmentName || "Attachment",
                                    comment.attachmentType || "",
                                  )
                                }
                                className={`d-flex align-items-center gap-2 p-3 rounded cursor-pointer ${isCurrentUser ? "bg-white text-primary" : "bg-primary-subtle"}`}
                                style={{ cursor: "pointer" }}
                              >
                                {getFileIcon(comment.attachmentType || "")}
                                <span className="text-truncate">{comment.attachmentName || "Attachment"}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        {isCurrentUser && (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white overflow-hidden flex-shrink-0"
                            style={{
                              width: "45px",
                              height: "45px",
                              minWidth: "45px",
                              minHeight: "45px",
                              background: "rgba(255, 255, 255, 0.2)",
                            }}
                          >
                            {comment.userPhotoURL ? (
                              <img
                                src={comment.userPhotoURL || "/placeholder.svg"}
                                alt={comment.userFullName}
                                className="w-100 h-100 rounded-circle"
                                style={{ objectFit: "cover" }}
                              />
                            ) : (
                              comment.userFullName.charAt(0).toUpperCase()
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="text-center text-white py-5">
                  <p className="fs-5 mb-2 opacity-75">No comments yet</p>
                  <p className="mb-0 opacity-50">Start the conversation</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {currentTicket.status === "open" && (
            <div className="border-top border-white border-opacity-25 p-4">
              <Form onSubmit={handleSubmitComment}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-white">Add Comment</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6} // doubled from 3 to 6 rows for desktop
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Type your message"
                    disabled={submitting}
                    className="bg-white border-0 rounded-3"
                  />
                </Form.Group>

                {attachment && (
                  <div
                    className="mb-3 p-3 rounded-3 d-flex justify-content-between align-items-center"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 text-white">
                      {getFileIcon(attachment.type)}
                      <span className="text-truncate">{attachment.name}</span>
                    </div>
                    <button className="p-0 text-white" onClick={clearAttachment}>
                      <XCircle className="size-4" />
                    </button>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center">
                  <button
                    className="btn-cancel-outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={submitting}
                  >
                    <Paperclip className="size-4 me-1" />
                    {t("attachFile")}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />

                  <button
                    type="submit"
                    className="btn-white-pill-bold"
                    disabled={(!newComment.trim() && !attachment) || submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        {t("sending")}
                      </>
                    ) : (
                      <>
                        <Send className="size-4 me-2" />
                        {t("sendMessage")}
                      </>
                    )}
                  </button>
                </div>
              </Form>
            </div>
          )}
        </div>
      </div>

      {/* Attachment Preview Modal */}
      {selectedAttachment && (
        <AttachmentPreviewModal
          show={showAttachmentModal}
          onHide={() => setShowAttachmentModal(false)}
          attachmentUrl={selectedAttachment.url}
          attachmentName={selectedAttachment.name}
          attachmentType={selectedAttachment.type}
        />
      )}
    </>
  )
}
